import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import core from "../../../vendors/core-api";
import { Card, Col, Container, Row } from "react-bootstrap";
import { GRAPH_COLORS } from "../../../constants";
import { getYear } from "date-fns";

const getValueInThousands = (value) => {
  if (value >= 1000) {
    return (value / 1000).toFixed(1) + "K";
  }
  return value.toFixed(0);
};

export const BarChart = ({ analytics = {} }) => {
  const { salesByYear = [] } = analytics;

  const getPreviousYearsSale = () => {
    const previousYearsSale = salesByYear.filter(
      ({ _id }) => _id.year < getYear(new Date()),
    );
    const averageSale =
      previousYearsSale.reduce(
        (current, { total_income }) => current + total_income,
        0,
      ) / previousYearsSale.length;

    return averageSale;
  };

  const getCurrentYearSale = () => {
    const yearSale = salesByYear
      .filter(({ _id }) => _id.year >= getYear(new Date()))
      .reduce((current, { total_income }) => current + total_income, 0);

    return yearSale;
  };

  const metrics = {
    series: [{ data: [getPreviousYearsSale(), getCurrentYearSale()] }],
    options: {
      chart: {
        type: "bar",
        height: 350,
      },
      colors: ["#8950fc", ...GRAPH_COLORS],
      plotOptions: {
        bar: {
          horizontal: true,
          dataLabels: {
            total: {
              enabled: true,
              offsetX: 0,
              style: {
                fontSize: "13px",
                fontWeight: 900,
              },
            },
          },
        },
      },
      stroke: {
        width: 1,
        colors: ["#fff"],
      },
      xaxis: {
        categories: ["Average", "This Year"],
        labels: {
          formatter: getValueInThousands,
        },
      },
      yaxis: {
        title: {
          text: undefined,
        },
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        y: {
          formatter: getValueInThousands,
          title: {
            formatter: function () {
              return "";
            },
          },
        },
      },
      fill: {
        opacity: 1,
      },
      legend: {
        position: "bottom",
        horizontalAlign: "left",
        offsetX: 40,
      },
    },
  };

  return (
    <Chart
      options={metrics.options}
      series={metrics.series}
      type="bar"
      height={"100%"}
      width="100%"
    />
  );
};

const AttendanceChart = ({ analytics = {} }) => {
  const { attendanceCount = [] } = analytics;

  const completedThisYear = attendanceCount
    .filter(({ _id }) => _id.year === getYear(new Date()))
    .reduce((current, { total_scanned }) => current + total_scanned, 0);
  const otherThisYear = attendanceCount
    .filter(({ _id }) => _id.year === getYear(new Date()))
    .reduce((current, { total }) => current + total, 0);

  const completedPrevYears = attendanceCount
    .filter(({ _id }) => _id.year !== getYear(new Date()))
    .reduce((current, { total_scanned }) => current + total_scanned, 0);
  const otherPrevYears = attendanceCount
    .filter(({ _id }) => _id.year !== getYear(new Date()))
    .reduce((current, { total }) => current + total, 0);

  const metrics = {
    series: [
      completedThisYear
        ? Math.ceil(
            (completedThisYear / (completedThisYear + otherThisYear)) * 100,
          )
        : 0,
      completedPrevYears
        ? Math.ceil(
            (completedPrevYears / (completedPrevYears + otherPrevYears)) * 100,
          )
        : 0,
    ],
    options: {
      chart: {
        type: "radialBar",
      },
      colors: ["#8950fc", ...GRAPH_COLORS],
      plotOptions: {
        radialBar: {
          dataLabels: {
            name: {
              fontSize: "22px",
            },
            value: {
              fontSize: "16px",
            },
            total: {
              show: true,
              label: "Attendance Rate",
            },
          },
        },
      },
      labels: ["This Year", "Previous Years"],
    },
  };

  return (
    <Chart
      options={metrics.options}
      series={metrics.series}
      type="radialBar"
      height={"100%"}
      width={"100%"}
    />
  );
};

const SalesByEventChart = ({ analytics = {} }) => {
  const { topSales = [] } = analytics;
  const topFive = topSales.slice(0, 4);

  const is5k = topFive[0]?.total_income >= 5000;

  const metrics = {
    series: [
      {
        data: topFive.map(({ total_income }) =>
          is5k
            ? Math.round((total_income / 1000 + Number.EPSILON) * 100) / 100
            : total_income,
        ),
      },
    ],
    options: {
      chart: {
        type: "bar",
        height: 350,
      },
      colors: ["#8950fc", ...GRAPH_COLORS],
      plotOptions: {
        bar: {
          borderRadius: 4,
          borderRadiusApplication: "end",
          horizontal: true,
        },
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories: topFive.map(({ event }) => event?.name),
        labels: {
          formatter: getValueInThousands,
        },
      },
      tooltip: {
        y: {
          title: {
            formatter: function () {
              return "";
            },
          },
          formatter: getValueInThousands,
        },
      },
    },
  };

  return (
    <Chart
      options={metrics.options}
      series={metrics.series}
      type="bar"
      height={"100%"}
      width={"100%"}
    />
  );
};

const EventsSalesMetrics = ({ store }) => {
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metrics, setMetrics] = useState({});

  useEffect(() => {
    async function fetchMetrics() {
      const api = core("pu").get();
      setMetricsLoading(true);

      const { data } = await api.get(`/store/${store._id}/event/analytics`);

      setMetrics(data);
      setMetricsLoading(false);
    }

    if (store && store._id) fetchMetrics();
  }, [store]);

  return (
    <>
      <Container>
        <Row xs={1} md={3} className="g-4 pt-4 pb-4">
          <Col>
            <Card style={{ height: 300 }}>
              <Card.Body>
                <AttendanceChart analytics={metrics?.analytics?.[0]} />
              </Card.Body>
            </Card>
          </Col>
          <Col>
            <Card style={{ height: 300 }}>
              <Card.Body>
                <Card.Title>Total Sales</Card.Title>
                <Card.Text style={{ height: "80%" }}>
                  <BarChart analytics={metrics?.analytics?.[0]} />
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col>
            <Card style={{ height: 300 }}>
              <Card.Body>
                <Card.Title>Top Events</Card.Title>
                <Card.Text style={{ height: "80%" }}>
                  <SalesByEventChart analytics={metrics?.analytics?.[0]} />
                </Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default EventsSalesMetrics;
